import {Component, Inject} from '@angular/core';
import {AccountConfig, PaymentFailureCategory, PaymentFailureContext} from '@app/interfaces/config/account.config';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import {PaymentService, PaymentStatusResponse, RetryFailedInvoiceResponse} from '@app/services/api/payment.service';
import {HttpClient} from '@angular/common/http';
import {environment} from '@src/environments/environment';

interface ReplaceCardSetupIntentResponse {
  stripe_client_secret: string;
  setup_intent_id: string;
}

@Component({
    selector: 'app-account-locked-dialog-component',
    templateUrl: './account-locked-dialog-component.component.html',
    styleUrls: ['./account-locked-dialog-component.component.scss'],
    standalone: false
})
export class AccountLockedDialogComponentComponent {
  public isLoading: boolean = false;
  public isRetrying: boolean = false;
  public isPreparingCardForm: boolean = false;
  public showCardForm: boolean = false;
  public stripeClientSecret: string | null = null;
  public stripePublishableKey: string | null = null;
  public accountConfig: AccountConfig;
  public paymentFailure: PaymentFailureContext | null = null;
  public retryError: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<any>,
    private paymentService: PaymentService,
    private http: HttpClient,
  ) {
    this.accountConfig = data.accountConfig;
    this.paymentFailure = this.accountConfig?.paymentFailure ?? null;
    this.stripePublishableKey = this.accountConfig?.stripePublishableKey ?? null;
  }

  static open(dialog: MatDialog, accountConfig: AccountConfig) {
    return dialog.open(AccountLockedDialogComponentComponent, {
      width: '600px',
      disableClose: true,
      data: {
        accountConfig
      }
    });
  }

  ngOnInit(): void {
  }

  get hasPaymentFailure(): boolean {
    return !!this.paymentFailure;
  }

  get failureCategory(): PaymentFailureCategory {
    return this.paymentFailure?.category ?? 'unknown';
  }

  retryPayment(): void {
    if (this.isRetrying) {
      return;
    }
    this.isRetrying = true;
    this.retryError = null;

    this.paymentService.retryFailedInvoice().subscribe({
      next: (res: RetryFailedInvoiceResponse) => {
        if (res.status === 'succeeded') {
          this.dialogRef.close(true);
          window.location.reload();
          return;
        }
        this.paymentFailure = {
          code: res.code ?? this.paymentFailure?.code ?? null,
          category: (res.category as PaymentFailureCategory | null) ?? this.paymentFailure?.category ?? 'unknown',
          message: res.message ?? this.paymentFailure?.message ?? null,
          invoiceId: this.paymentFailure?.invoiceId ?? null,
          failedAt: this.paymentFailure?.failedAt ?? null,
        };
        this.retryError = res.message ?? 'failed';
        this.isRetrying = false;
      },
      error: (err) => {
        this.retryError = err?.error?.message ?? err?.message ?? 'unknown_error';
        this.isRetrying = false;
      }
    });
  }

  /**
   * Kick off the replace-card flow: request a fresh SetupIntent, then reveal
   * the embedded Stripe Elements card form (PaymentAuthorizationModalComponent).
   * After the user authorizes the new card we hand the setup_intent_id to the
   * server which swaps the stored card and re-runs the failed invoice charge.
   */
  startUpdateCard(): void {
    if (this.isPreparingCardForm || this.showCardForm) {
      return;
    }
    this.isPreparingCardForm = true;
    this.retryError = null;

    this.http.post<ReplaceCardSetupIntentResponse>(
      `${environment.apiUrl}/api/payments/replace-card/setup-intent`,
      {},
    ).subscribe({
      next: (res) => {
        this.stripeClientSecret = res.stripe_client_secret;
        this.showCardForm = true;
        this.isPreparingCardForm = false;
      },
      error: (err) => {
        this.retryError = err?.error?.message ?? 'Failed to start card update';
        this.isPreparingCardForm = false;
      }
    });
  }

  onCardAuthorized(event: PaymentStatusResponse): void {
    const setupIntentId = (event as any)?.setup_intent_id;
    if (!setupIntentId) {
      this.retryError = 'Missing setup intent id';
      return;
    }
    this.isRetrying = true;
    this.retryError = null;

    this.http.post<RetryFailedInvoiceResponse>(
      `${environment.apiUrl}/api/payments/replace-card/confirm`,
      {setup_intent_id: setupIntentId},
    ).subscribe({
      next: (res) => {
        if (res.status === 'succeeded') {
          this.dialogRef.close(true);
          window.location.reload();
          return;
        }
        // Card replaced but new charge also failed — show the refreshed error,
        // collapse the form so the user can retry or contact support.
        this.paymentFailure = {
          code: res.code ?? null,
          category: (res.category as PaymentFailureCategory | null) ?? 'unknown',
          message: res.message ?? null,
          invoiceId: this.paymentFailure?.invoiceId ?? null,
          failedAt: this.paymentFailure?.failedAt ?? null,
        };
        this.retryError = res.message ?? 'failed';
        this.showCardForm = false;
        this.stripeClientSecret = null;
        this.isRetrying = false;
      },
      error: (err) => {
        this.retryError = err?.error?.message ?? err?.message ?? 'unknown_error';
        this.showCardForm = false;
        this.stripeClientSecret = null;
        this.isRetrying = false;
      }
    });
  }

  onCardFormCancelled(): void {
    this.showCardForm = false;
    this.stripeClientSecret = null;
  }

  onCardFormFailed(message: string): void {
    this.retryError = message;
    this.showCardForm = false;
    this.stripeClientSecret = null;
  }

  /**
   * Stripe rejected the new card during confirmCardSetup (decline, validation,
   * issuer hold, etc). Keep the embedded card form open — its internal red
   * banner now renders correctly under `[embedded]="true"` — so the user can
   * enter another card without re-opening the SetupIntent flow. We still
   * refresh the parent's failure context in case they cancel back out.
   */
  onCardSetupError(event: { code: string | null; declineCode: string | null; message: string }): void {
    const code = event.declineCode || event.code || 'card_error';
    const category = this.classifyStripeError(code);

    this.paymentFailure = {
      code,
      category,
      message: event.message,
      invoiceId: this.paymentFailure?.invoiceId ?? null,
      failedAt: this.paymentFailure?.failedAt ?? new Date().toISOString(),
    };
    this.retryError = event.message;
    this.isRetrying = false;
    // Intentionally leave showCardForm/stripeClientSecret untouched so the
    // user can retry with another card without reissuing a SetupIntent.
  }

  private classifyStripeError(code: string): PaymentFailureCategory {
    const issuerDeclines = new Set([
      'card_velocity_exceeded',
      'card_declined',
      'generic_decline',
      'insufficient_funds',
      'lost_card',
      'stolen_card',
      'pickup_card',
      'do_not_honor',
      'fraudulent',
      'transaction_not_allowed',
      'restricted_card',
      'service_not_allowed',
      'security_violation',
      'stop_payment_order',
      'withdrawal_count_limit_exceeded',
      'currency_not_supported',
      'try_again_later',
      'call_issuer',
      'authentication_required',
    ]);
    const recoverable = new Set([
      'expired_card',
      'incorrect_cvc',
      'incorrect_number',
      'incorrect_zip',
      'invalid_cvc',
      'invalid_expiry_month',
      'invalid_expiry_year',
      'invalid_number',
      'invalid_account',
      'invalid_pin',
      'processing_error',
      'card_not_supported',
      'requires_action',
    ]);
    if (issuerDeclines.has(code)) return 'issuer_decline';
    if (recoverable.has(code)) return 'recoverable';
    return 'unknown';
  }

  submit() {
    this.dialogRef.close(true);
  }

  close() {
    this.dialogRef.close(false);
  }
}
